import { Box, Chip, Dialog, DialogContent, IconButton, Tooltip, Typography } from '@mui/material';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import CloseIcon from '@mui/icons-material/Close';
import { useLiveMatch } from '../LiveMatchContext.tsx';
import { FieldPosition, LivePlayer, TeamSide } from '../liveMatchTypes.ts';

// HOME (sinistra): col 0 = fondo, col 1 = vicino alla rete
const HOME_COLS: number[][] = [
  [5, 6, 1],
  [4, 3, 2],
];

// AWAY (destra): col 0 = vicino alla rete, col 1 = fondo
const AWAY_COLS: number[][] = [
  [2, 3, 4],
  [1, 6, 5],
];

const ROLE_SHORT: Record<string, string> = {
  PALLEGGIO: 'PAL',
  OPPOSTO:   'OPP',
  BANDA:     'BAN',
  CENTRALE:  'CEN',
  LIBERO:    'LIB',
};

interface CircleProps {
  pos: number;
  fieldPos: FieldPosition | undefined;
  player: LivePlayer | undefined;
  shirtColor: string;
  setterColor: string;
  enlarged?: boolean;
  onPlayerClick?: (playerId: number) => void;
}

function PlayerCircle({ pos, fieldPos, player, shirtColor, setterColor, enlarged = false, onPlayerClick }: CircleProps) {
  const isLibero        = fieldPos?.isLibero ?? false;
  const isEmpty         = !fieldPos || !player;
  const isPalleggiatore = fieldPos?.role === 'PALLEGGIO';

  const circleBg    = isLibero ? '#f9a825' : isEmpty ? 'rgba(255,255,255,0.08)' : isPalleggiatore ? '#000' : shirtColor;
  const circleColor = isLibero ? '#000' : isPalleggiatore ? '#FFD700' : '#fff';
  const borderColor = isLibero ? '#e65100' : isEmpty ? 'rgba(255,255,255,0.2)' : isPalleggiatore ? '#FFD700' : `${shirtColor}bb`;

  const roleLabel    = fieldPos?.role ? (ROLE_SHORT[fieldPos.role] ?? fieldPos.role[0]) : null;
  const tooltipTitle = player
    ? `${pos} · ${player.firstName} ${player.lastName}${fieldPos?.role ? ` · ${fieldPos.role}` : ''}`
    : `Pos ${pos} — vuota`;

  const sz  = enlarged ? { xs: 96, sm: 116, md: 136 } : { xs: 64, sm: 76, md: 88 };
  const num = enlarged ? { xs: '1.6rem', sm: '2rem', md: '2.4rem' } : { xs: '1.1rem', sm: '1.35rem', md: '1.55rem' };
  const lbl = enlarged ? { xs: 12, sm: 14, md: 16 } : { xs: 10, sm: 11, md: 12 };

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box
        onClick={() => !isEmpty && player && onPlayerClick?.(player.id)}
        sx={{
          width: sz, height: sz,
          borderRadius: '50%',
          bgcolor: circleBg,
          color: circleColor,
          border: '2.5px solid',
          borderColor,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          cursor: isEmpty ? 'default' : 'pointer',
          flexShrink: 0,
          transition: 'transform 0.15s, filter 0.15s',
          animation: 'circle-appear 0.45s ease-out',
          '@keyframes circle-appear': {
            '0%':   { transform: 'scale(0.5)', opacity: 0 },
            '70%':  { transform: 'scale(1.1)', opacity: 1 },
            '100%': { transform: 'scale(1)',   opacity: 1 },
          },
          '&:hover': isEmpty ? {} : { transform: 'scale(1.1)', filter: 'brightness(1.15)' },
        }}
      >
        {player ? (
          <>
            <Typography sx={{ fontSize: num, fontWeight: 700, lineHeight: 1, color: 'inherit' }}>
              {player.shirtNumber || '?'}
            </Typography>
            {roleLabel && (
              <Typography sx={{ fontSize: lbl, lineHeight: 1, mt: '2px', color: 'inherit', opacity: 0.85, fontWeight: 600 }}>
                {roleLabel}
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: 10, opacity: 0.25, color: 'rgba(255,255,255,0.5)' }}>
            {pos}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

interface TeamColumnsProps {
  side: TeamSide;
  cols: number[][];
  enlarged?: boolean;
  onPlayerClick?: (playerId: number) => void;
}

function TeamColumns({ side, cols, enlarged = false, onPlayerClick }: TeamColumnsProps) {
  const { state }   = useLiveMatch();
  const lineup      = side === 'HOME' ? state.homeLineup     : state.awayLineup;
  const callup      = side === 'HOME' ? state.homeCallup     : state.awayCallup;
  const shirtColor  = side === 'HOME' ? state.homeShirtColor : state.awayShirtColor;
  const setterColor = side === 'HOME' ? state.awayShirtColor : state.homeShirtColor;

  const posMap = new Map(lineup.map((fp) => [fp.position, fp]));
  const gap    = enlarged ? { xs: 1.5, sm: 2, md: 2.5 } : { xs: 1, sm: 1.5, md: 2 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap }}>
      {cols.map((col, ci) => (
        <Box key={ci} sx={{ display: 'flex', flexDirection: 'column', gap }}>
          {col.map((pos) => {
            const fp     = posMap.get(pos);
            const player = fp ? callup.find((p) => p.id === fp.playerId) : undefined;
            const key    = `${pos}-${fp?.playerId ?? 'empty'}`;
            const circle = (
              <PlayerCircle
                pos={pos} fieldPos={fp} player={player}
                shirtColor={shirtColor} setterColor={setterColor}
                enlarged={enlarged}
                onPlayerClick={onPlayerClick}
              />
            );
            return pos === 1 ? (
              <Box key={key} sx={{ p: 0.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)', border: '1px dashed rgba(255,255,255,0.4)' }}>
                {circle}
              </Box>
            ) : (
              <PlayerCircle
                key={key}
                pos={pos} fieldPos={fp} player={player}
                shirtColor={shirtColor} setterColor={setterColor}
                enlarged={enlarged}
                onPlayerClick={onPlayerClick}
              />
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

function LiberoIndicator({ player, enlarged = false }: { player: LivePlayer; enlarged?: boolean }) {
  const sz  = enlarged ? { xs: 68, sm: 84, md: 100 } : { xs: 44, sm: 52, md: 60 };
  const num = enlarged ? { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' } : { xs: '0.8rem', sm: '0.95rem', md: '1.1rem' };
  const lbl = enlarged ? { xs: 12, sm: 13, md: 14 } : { xs: 9, sm: 10, md: 11 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4, alignSelf: 'center' }}>
      <Typography sx={{ fontSize: 9, color: '#f9a825', opacity: 0.75, letterSpacing: 1 }}>L</Typography>
      <Tooltip title={`Libero · ${player.firstName} ${player.lastName}`} arrow placement="top">
        <Box sx={{
          width: sz, height: sz, borderRadius: '50%',
          bgcolor: '#f9a825', border: '2.5px solid #e65100',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          animation: 'circle-appear 0.45s ease-out',
          '@keyframes circle-appear': {
            '0%':   { transform: 'scale(0.5)', opacity: 0 },
            '70%':  { transform: 'scale(1.1)', opacity: 1 },
            '100%': { transform: 'scale(1)',   opacity: 1 },
          },
        }}>
          <Typography sx={{ fontSize: num, fontWeight: 700, lineHeight: 1, color: '#000' }}>
            {player.shirtNumber || '?'}
          </Typography>
          <Typography sx={{ fontSize: lbl, lineHeight: 1, mt: '1px', fontWeight: 700, color: '#000' }}>L</Typography>
        </Box>
      </Tooltip>
    </Box>
  );
}

interface VolleyballFieldProps {
  onPlayerClick?: (playerId: number, side: TeamSide) => void;
  swapped?: boolean;
  enlarged?: boolean;
}

function getBenchLibero(side: TeamSide, state: ReturnType<typeof useLiveMatch>['state']): LivePlayer | undefined {
  const liberoId = side === 'HOME' ? state.homeLiberoId : state.awayLiberoId;
  if (liberoId === null) return undefined;
  const lineup = side === 'HOME' ? state.homeLineup : state.awayLineup;
  if (lineup.some((fp) => fp.playerId === liberoId)) return undefined;
  const callup = side === 'HOME' ? state.homeCallup : state.awayCallup;
  return callup.find((p) => p.id === liberoId);
}

// ─── FieldLayout ─────────────────────────────────────────────────────────────

function FieldLayout({ onPlayerClick, swapped = false, enlarged = false }: VolleyballFieldProps) {
  const { state } = useLiveMatch();

  const leftSide  = swapped ? 'AWAY' : 'HOME';
  const rightSide = swapped ? 'HOME' : 'AWAY';
  const leftName  = swapped ? state.awayTeamName : state.homeTeamName;
  const rightName = swapped ? state.homeTeamName : state.awayTeamName;

  const leftLibero  = getBenchLibero(leftSide,  state);
  const rightLibero = getBenchLibero(rightSide, state);
  const mx = enlarged ? { xs: 2, sm: 3, md: 4 } : { xs: 1.5, sm: 2.5, md: 3.5 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', py: 1.5, px: 1 }}>

      {/* SINISTRA */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: enlarged ? 1.5 : 0.75 }}>
        <Typography sx={{
          fontSize: enlarged ? { xs: '1rem', sm: '1.25rem', md: '1.5rem' } : { xs: 9, sm: 11 },
          fontWeight: enlarged ? 700 : 400,
          opacity: enlarged ? 0.9 : 0.35,
          color: 'white',
          letterSpacing: enlarged ? 0.5 : 0,
        }}>{leftName}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
          {leftLibero && <LiberoIndicator player={leftLibero} enlarged={enlarged} />}
          <TeamColumns side={leftSide} cols={HOME_COLS} enlarged={enlarged}
            onPlayerClick={onPlayerClick ? (id) => onPlayerClick(id, leftSide) : undefined} />
        </Box>
      </Box>

      {/* RETE */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mx, alignSelf: 'stretch', pt: '1.4rem' }}>
        <Box sx={{ width: enlarged ? 7 : 5, flex: 1, bgcolor: 'grey.500', borderRadius: 0.5, position: 'relative' }}>
          <Chip label="RETE" size="small" sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            height: 16, fontSize: 9, bgcolor: 'grey.600',
            color: 'rgba(255,255,255,0.7)', letterSpacing: 1,
            '& .MuiChip-label': { px: 0.75 },
          }} />
        </Box>
      </Box>

      {/* DESTRA */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: enlarged ? 1.5 : 0.75 }}>
        <Typography sx={{
          fontSize: enlarged ? { xs: '1rem', sm: '1.25rem', md: '1.5rem' } : { xs: 9, sm: 11 },
          fontWeight: enlarged ? 700 : 400,
          opacity: enlarged ? 0.9 : 0.35,
          color: 'white',
          letterSpacing: enlarged ? 0.5 : 0,
        }}>{rightName}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
          <TeamColumns side={rightSide} cols={AWAY_COLS} enlarged={enlarged}
            onPlayerClick={onPlayerClick ? (id) => onPlayerClick(id, rightSide) : undefined} />
          {rightLibero && <LiberoIndicator player={rightLibero} enlarged={enlarged} />}
        </Box>
      </Box>
    </Box>
  );
}

// ─── VolleyballField (export) ─────────────────────────────────────────────────

export default function VolleyballField({ onPlayerClick, swapped = false }: Omit<VolleyballFieldProps, 'enlarged'>) {
  return <FieldLayout onPlayerClick={onPlayerClick} swapped={swapped} />;
}

// ─── FieldZoomDialog ──────────────────────────────────────────────────────────

interface ZoomProps {
  open: boolean;
  onClose: () => void;
  swapped?: boolean;
}

export function FieldZoomDialog({ open, onClose, swapped = false }: ZoomProps) {
  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { bgcolor: '#1a1a2e' } }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', bgcolor: 'rgba(0,0,0,0.4)' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <FieldLayout swapped={swapped} enlarged />
      </DialogContent>
    </Dialog>
  );
}

// ─── ZoomButton ───────────────────────────────────────────────────────────────

export function FieldZoomButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip title="Ingrandisci campo" placement="left">
      <IconButton
        onClick={onClick}
        size="small"
        sx={{
          color: 'rgba(255,255,255,0.45)',
          bgcolor: 'rgba(255,255,255,0.06)',
          '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.15)' },
        }}
      >
        <FitScreenIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
